import React, { useEffect } from "react";
import { useParams, Navigate } from "react-router-dom";
import LocalizedLink from "@/components/LocalizedLink";
import { useTranslation } from "react-i18next";
import { findPackageBySlug } from "@/data/packages";
import { useContact } from "@/context/ContactContext";
import { trackEvent } from "@/utils/analytics";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import "./PackagePage.scss";

const ArrowIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M3 8h10M9 4l4 4-4 4" />
  </svg>
);

interface PackageContent {
  name: string;
  tagline: string;
  priceBand: string;
  priceNote: string;
  timeline: string;
  forWho: string;
  includes: string[];
  deliverables: string[];
  faq: { q: string; a: string }[];
  ctaLabel: string;
}

const PackagePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const { requestContact } = useContact();

  const pkg = slug ? findPackageBySlug(slug) : undefined;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [slug]);

  useEffect(() => {
    if (pkg) {
      trackEvent("package_page_view", { package: pkg.slug });
    }
  }, [pkg]);

  const content = pkg
    ? (t(`packages.items.${pkg.key}`, { returnObjects: true }) as PackageContent)
    : null;

  useDocumentMeta(
    pkg && content
      ? {
          title: `${content.name} — Devbloom`,
          description: content.tagline,
          canonical: `https://devbloom.dev/packages/${pkg.slug}`,
          ogTitle: `${content.name} — Devbloom`,
          ogDescription: content.tagline,
          ogType: "website",
        }
      : null
  );

  if (!pkg || !content) {
    return <Navigate to="/" replace />;
  }

  return (
    <article className="package-page">
      <div className="container">
        <LocalizedLink to="/#packages-section" className="pp-back">
          {t("packages.page.backToPackages")}
        </LocalizedLink>

        {/* Hero */}
        <header className="pp-hero">
          <div className="pp-kicker">
            <span className="pp-timeline-pill">{content.timeline}</span>
            <span className="pp-price">{content.priceBand}</span>
            <span className="pp-price-note">{content.priceNote}</span>
          </div>
          <h1 className="pp-title">{content.name}</h1>
          <p className="pp-tagline">{content.tagline}</p>
          <div className="pp-tags">
            {pkg.tags.map((tag) => (
              <span className="pp-tag" key={tag}>{tag}</span>
            ))}
          </div>
        </header>

        {/* Who it's for */}
        <section className="pp-section">
          <h2 className="pp-section-label">{t("packages.page.forWhoLabel")}</h2>
          <p className="pp-body">{content.forWho}</p>
        </section>

        {/* What's included */}
        <section className="pp-section">
          <h2 className="pp-section-label">{t("packages.page.includesLabel")}</h2>
          <ul className="pp-list pp-list--includes">
            {content.includes.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </section>

        {/* Deliverables */}
        <section className="pp-section">
          <h2 className="pp-section-label">{t("packages.page.deliverablesLabel")}</h2>
          <ul className="pp-list pp-list--deliverables">
            {content.deliverables.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </section>

        {/* FAQ */}
        {content.faq.length > 0 && (
          <section className="pp-section">
            <h2 className="pp-section-label">{t("packages.page.faqLabel")}</h2>
            <dl className="pp-faq">
              {content.faq.map((item, i) => (
                <React.Fragment key={i}>
                  <dt className="pp-faq-q">{item.q}</dt>
                  <dd className="pp-faq-a">{item.a}</dd>
                </React.Fragment>
              ))}
            </dl>
          </section>
        )}

        {/* CTA */}
        <section className="pp-cta">
          <p className="pp-cta-sub">{t("packages.page.ctaSub")}</p>
          <button
            className="pp-cta-btn"
            onClick={() => {
              trackEvent("package_card_cta", { package: pkg.slug });
              requestContact(content.ctaLabel);
            }}
          >
            {t("packages.bookCta")} <ArrowIcon />
          </button>
        </section>
      </div>
    </article>
  );
};

export default PackagePage;
